import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useLazyQuery } from "react-apollo";
import { Label } from "rbx";
import { uuidGenerator, ourToast as toast } from "../../utils";

import InspectionItem from "./InspectionItem";
import {
  UPDATE_ALL_INSPECTIONS_MUTATION,
  DELETE_INSPECTION_MUTATION,
  DELETE_ALL_INSPECTIONS_MUTATION
} from "../../graphql/Inspections/Inspections.graphql";
import { SINGLE_INSPECTION_MASTER_QUERY } from "../../graphql/InspectionMaster/InspectionMaster.graphql";

import Loading from "../Loading";
import { ModalContext } from "../ModalProvider";
import { Confirmation } from "../modals";
import PageHeader from "./PageHeader";
import { INSPECTION_COLUMNS } from "../../pages/FleetManagement/Inspections/columns";

import "./InspectionItems.scss";

import { useAuthorization } from "../Authorization";

function InspectionItems({
  dataQuery,
  where,
  goBack,
  inspectionMasterRecIdRef
}) {
  const MechanicAuth = useAuthorization(["MECH"]);
  const AdminAuth = useAuthorization(["ADMIN"]);
  const FleetAuth = useAuthorization(["FLEET"]);
  const [items, setItems] = useState([]);

  const { InspectionMasterRecIdRef } = where;
  const { setModalOpen } = useContext(ModalContext);

  const [getInspectionMaster, { data: InspectionMasterData }] = useLazyQuery(
    SINGLE_INSPECTION_MASTER_QUERY
  );

  useEffect(() => {
    if (inspectionMasterRecIdRef) {
      getInspectionMaster({
        variables: { where: { RecId: parseInt(inspectionMasterRecIdRef, 10) } }
      });
    }
  }, [getInspectionMaster, inspectionMasterRecIdRef]);

  const { data: InspectionsData } = useQuery(dataQuery, {
    variables: {
      where
    },
    fetchPolicy: "cache-and-network"
  });

  const [updateAllInspections, res] = useMutation(
    UPDATE_ALL_INSPECTIONS_MUTATION,
    {
      refetchQueries: ["ALL_INSPECTIONS_QUERY"]
    }
  );
  const [deleteInspection, res2] = useMutation(DELETE_INSPECTION_MUTATION, {
    refetchQueries: ["ALL_INSPECTIONS_QUERY"]
  });

  const [deleteAllInspections, res3] = useMutation(
    DELETE_ALL_INSPECTIONS_MUTATION,
    {
      refetchQueries: ["DELETE_ALL_INSPECTIONS_MUTATION"]
    }
  );

  useEffect(() => {
    const inspections = InspectionsData?.Inspections;
    if (Array.isArray(inspections)) {
      setItems(inspections);
    }
  }, [InspectionsData]);

  const resetOrderItems = newInputs => {
    const currInputs = newInputs.map((item, i) => ({
      ...item,
      LineItem: i + 1
    }));

    setItems(currInputs);
  };

  const removeRow = async (index, item) => {
    const currInputs = [...items];
    currInputs.splice(index, 1);
    if (typeof item.RecId === "string") {
      resetOrderItems(currInputs);
    } else {
      setModalOpen(
        true,
        <Confirmation
          message="Are you sure you want to delete this item?"
          onConfirm={async () => {
            setModalOpen(false);
            await deleteInspection({
              variables: {
                where: {
                  RecId: item.RecId
                }
              }
            });
            await resetOrderItems(currInputs);
            toast.success("Item deleted successfully.");
          }}
        />
      );
    }
  };

  const handleChange = () => e => {
    const newData = items.map(i =>
      i.RecId === e.target.name.split("_")[1] ||
      i.RecId === parseInt(e.target.name.split("_")[1])
        ? {
            ...i,
            [e.target.name.split("_")[0]]: e.target.value
          }
        : i
    );
    setItems(newData);
  };

  const insertAfter = index => () => {
    const currInputs = [...items];
    currInputs.splice(index + 1, 0, {
      LineItem: index + 2,
      Description: "",
      RecId: `tg${uuidGenerator()}`,
      InspectionMasterRecIdRef: parseInt(InspectionMasterRecIdRef, 10),
      InspecCdRecIdRef:
        InspectionMasterData?.InspectionMaster?.InspecCdRecIdRef,
      VehicleTypeRecIdRef:
        InspectionMasterData?.InspectionMaster?.VehicleTypeRecIdRef,
      Active: true
    });
    resetOrderItems(currInputs);
  };

  const onSaveClick = async () => {
    try {
      if (items.find(d => d.Description === "" || d.Description === " ")) {
        toast.error("Description is required.");
      } else {
        const dataToSend = items.map(i => {
          const copy = i;
          delete copy.__typename;
          if (typeof copy.RecId === "string") {
            delete copy.RecId;
          }
          return copy;
        });
        await updateAllInspections({
          variables: {
            data: dataToSend
          }
        });
        setItems([]);
        toast.success("Inspections items updated successfully.");
      }
    } catch (error) {
      toast.error("Error on update Inspections Items.");
    }
  };

  const onDeleteAllClick = () => {
    if (items && items.length > 0) {
      const DataToSend = items.filter(item => typeof item.RecId === "number");
      if (DataToSend.length > 0) {
        setModalOpen(
          true,
          <Confirmation
            message="Are you sure you want to delete ALL the items?"
            onConfirm={async () => {
              setModalOpen(false);
              await deleteAllInspections({
                variables: {
                  data: DataToSend.map(item => ({ RecId: item.RecId }))
                }
              });
              toast.success("Items deleted successfully.");
              goBack();
            }}
          />
        );
      } else {
        setItems([]);
      }
    }
  };

  const inspectionDescription =
    InspectionMasterData?.InspectionMaster?.InspectionCode?.CodeDescription;
  const vehicleType =
    InspectionMasterData?.InspectionMaster?.VehicleType?.CodeDescription;

  const subTitle =
    inspectionDescription && vehicleType
      ? `Inspection Description: ${inspectionDescription} - Vehicle Type: ${vehicleType}`
      : "";

  return (
    <React.Fragment>
      {/* <pre>{JSON.stringify(InspectionMasterData, null, 2)}</pre> */}
      <PageHeader
        actionsType="inspections"
        columns={INSPECTION_COLUMNS}
        editing={res.loading || res2.loading}
        filters={where}
        name="Inspections"
        query={dataQuery}
        readOnly={MechanicAuth && (!AdminAuth || !FleetAuth)}
        subTitle={subTitle}
        title="Inspections"
        onDeleteAllClick={onDeleteAllClick}
        onSaveClick={onSaveClick}
      />
      <div className="directions-input-header">
        <div />
        <div>
          <Label>Line #</Label>
        </div>
        <div>
          <Label>Description</Label>
        </div>
        <div />
      </div>

      {items.map((item, i) => (
        <InspectionItem
          disabled={
            (MechanicAuth && !AdminAuth) ||
            res.loading ||
            res2.loading ||
            res3.loading
          }
          handleChange={handleChange(i)}
          insertAfter={insertAfter(i)}
          item={item}
          key={item.RecId}
          removeRow={() => removeRow(i, item)}
        />
      ))}

      {(res.loading || res2.loading || res3.loading) && <Loading />}
    </React.Fragment>
  );
}

InspectionItems.propTypes = {
  dataQuery: PropTypes.object,
  where: PropTypes.object,
  goBack: PropTypes.func,
  inspectionMasterRecIdRef: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ])
};
export default InspectionItems;
